import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';

const EnhancedTextureContext = createContext();
const TextureContext = createContext(); // Legacy context for backward compatibility

const TEXTURE_STORAGE_KEY = 'megalith_texture_enabled';
const TEXTURE_QUALITY_KEY = 'megalith_texture_quality';
const TEXTURE_ENABLED_CLASS = 'textures-enabled';
const TEXTURE_DISABLED_CLASS = 'textures-disabled';

// Texture quality settings based on device capabilities
const TEXTURE_QUALITIES = {
  high: {
    opacity: { light: 0.15, dark: 0.25 },
    size: '300px',
    format: 'webp',
    fallback: 'png'
  },
  medium: {
    opacity: { light: 0.12, dark: 0.20 },
    size: '200px',
    format: 'webp',
    fallback: 'png'
  },
  low: {
    opacity: { light: 0.08, dark: 0.15 },
    size: '150px',
    format: 'png',
    fallback: 'png'
  }
};

// Detect device capabilities
const detectDeviceCapabilities = () => {
  const isHighDPI = window.devicePixelRatio > 1.5;
  const isSlowConnection = navigator.connection?.effectiveType === 'slow-2g' || 
                          navigator.connection?.effectiveType === '2g';
  const isLowEndDevice = navigator.hardwareConcurrency <= 2;
  
  if (isSlowConnection || isLowEndDevice) return 'low';
  if (isHighDPI) return 'high';
  return 'medium';
};

export const EnhancedTextureProvider = ({ children }) => {
  // Initialize texture enabled state from localStorage
  const [isTextureEnabled, setIsTextureEnabled] = useState(() => {
    try {
      const storedValue = localStorage.getItem(TEXTURE_STORAGE_KEY);
      return storedValue === null ? true : JSON.parse(storedValue);
    } catch (error) {
      console.error("Error reading texture preference from localStorage:", error);
      return true;
    }
  });

  // Initialize texture quality from localStorage or auto-detect
  const [textureQuality, setTextureQuality] = useState(() => {
    try {
      const storedQuality = localStorage.getItem(TEXTURE_QUALITY_KEY);
      return storedQuality || detectDeviceCapabilities();
    } catch (error) {
      console.error("Error reading texture quality from localStorage:", error);
      return detectDeviceCapabilities();
    }
  });

  // Update localStorage and body class when state changes
  useEffect(() => {
    try {
      localStorage.setItem(TEXTURE_STORAGE_KEY, JSON.stringify(isTextureEnabled));
      localStorage.setItem(TEXTURE_QUALITY_KEY, textureQuality);
    } catch (error) {
      console.error("Error saving texture preferences to localStorage:", error);
    }

    // Update body classes
    document.body.classList.remove(TEXTURE_ENABLED_CLASS, TEXTURE_DISABLED_CLASS);
    document.body.classList.add(isTextureEnabled ? TEXTURE_ENABLED_CLASS : TEXTURE_DISABLED_CLASS);
    
    // Add quality class
    document.body.classList.remove('texture-high', 'texture-medium', 'texture-low');
    document.body.classList.add(`texture-${textureQuality}`);

    return () => {
      document.body.classList.remove(
        TEXTURE_ENABLED_CLASS, 
        TEXTURE_DISABLED_CLASS,
        'texture-high', 
        'texture-medium', 
        'texture-low'
      );
    };
  }, [isTextureEnabled, textureQuality]);

  // Memoized toggle function
  const toggleTextureEnabled = useCallback(() => {
    setIsTextureEnabled(prev => !prev);
  }, []);

  // Function to change texture quality
  const setQuality = useCallback((quality) => {
    if (TEXTURE_QUALITIES[quality]) {
      setTextureQuality(quality);
    }
  }, []);

  // Get texture styles for a component
  const getTextureStyles = useCallback((textureName, colorMode = 'light') => {
    if (!isTextureEnabled) {
      return {
        backgroundImage: 'none',
        opacity: 0
      };
    }

    const quality = TEXTURE_QUALITIES[textureQuality];
    const textureOpacity = quality.opacity[colorMode] || quality.opacity.light;
    
    return {
      backgroundImage: `url("/textures/${textureName}.${quality.format}")`,
      backgroundRepeat: 'repeat',
      backgroundSize: quality.size,
      opacity: textureOpacity,
      mixBlendMode: colorMode === 'dark' ? 'multiply' : 'overlay',
      transition: 'opacity 0.3s ease-in-out'
    };
  }, [isTextureEnabled, textureQuality]);

  // Get full component background styles (spans entire component)
  const getFullBackgroundStyles = useCallback((textureName, colorMode = 'light', additionalStyles = {}) => {
    const textureStyles = getTextureStyles(textureName, colorMode);
    
    return {
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -1,
        pointerEvents: 'none',
        ...textureStyles,
        ...additionalStyles
      }
    };
  }, [getTextureStyles]);

  const value = {
    isTextureEnabled,
    textureQuality,
    toggleTextureEnabled,
    setQuality,
    getTextureStyles,
    getFullBackgroundStyles,
    qualities: Object.keys(TEXTURE_QUALITIES)
  };

  return (
    <EnhancedTextureContext.Provider value={value}>
      {children}
    </EnhancedTextureContext.Provider>
  );
};

// Custom hook for easy consumption
export const useEnhancedTexture = () => {
  const context = useContext(EnhancedTextureContext);
  if (context === undefined) {
    throw new Error('useEnhancedTexture must be used within an EnhancedTextureProvider');
  }
  return context;
};

// Legacy hook for backward compatibility
export const useTexture = () => {
  const context = useContext(EnhancedTextureContext);
  if (context === undefined) {
    throw new Error('useTexture must be used within an EnhancedTextureProvider');
  }
  return {
    isTextureEnabled: context.isTextureEnabled,
    toggleTextureEnabled: context.toggleTextureEnabled
  };
};

// Legacy provider alias for backward compatibility
export const TextureProvider = EnhancedTextureProvider;
